'use client'

import React, { ReactNode } from 'react'
import { FieldError, Message } from 'react-hook-form'
import { FormHelpText } from '../FormHelpText/FormHelpText'
import { FormInputLabel } from '../FormInputLabel/FormInputLabel'
import { FormInputValidationText } from '../FormInputValidationText/FormInputValidationText'
import { Tooltip } from '../../atoms'

const ROOT_CLASS = 'form-field'

export interface FormFieldProps extends React.HTMLAttributes<HTMLDivElement> {
    label?: ReactNode
    disabled?: boolean
    readOnly?: boolean
    error?: string | Message | FieldError | ReactNode
    showError?: boolean
    name: string
    /**
     * Only use helpText on inputs that don't share the same row as other FormFields. Doing that will cause the Inputs to be misaligned.
     */
    helpText?: ReactNode
    optional?: boolean
    isQuiet?: boolean
    /** Hides the label visually without hiding it for screen readers */
    screenReaderOnly?: boolean
    tooltip?: ReactNode
    children: ReactNode
}

export const FormField = ({
    disabled = false,
    readOnly = false,
    error,
    showError = true,
    name,
    label,
    helpText,
    optional = false,
    isQuiet = false,
    screenReaderOnly = false,
    tooltip,
    children,
    ...props
}: FormFieldProps) => {
    const getClasses = (): string => {
        let classes: string[] = [ROOT_CLASS]

        if (props.className) classes.push(props.className)
        if (error) classes.push('error')
        if (disabled) classes.push('disabled')
        if (readOnly) classes.push('readonly')
        if (isQuiet) classes.push('quiet')
        if (screenReaderOnly) classes.push('screenreader-only')

        return classes.join(' ')
    }

    const getTestId = (): string => {
        let testId = 'form-field'
        if (error) testId += '--has-error'
        return testId
    }

    return (
        <div {...props} className={getClasses()} data-testid={getTestId()}>
            {label && (
                <div className={`inline-flex gap-1 ${screenReaderOnly && 'sr-only'}`}>
                    <div className="block line-clamp-1 overflow-ellipsis whitespace-nowrap">
                        <FormInputLabel name={name} optional={optional} disabled={disabled}>
                            {label}
                        </FormInputLabel>
                    </div>
                    {tooltip && <Tooltip content={tooltip} filled />}
                </div>
            )}
            {helpText && !error && (
                <div className="block help-text">
                    <FormHelpText>{helpText}</FormHelpText>
                </div>
            )}
            <div
                className={`${ROOT_CLASS}__input block ${
                    (label && !screenReaderOnly) || helpText || (error && showError) ? 'mt-2' : ''
                }`}
            >
                {children}
            </div>
            {error && showError && (
                <div className="block">
                    <FormInputValidationText>
                        <>{error}</>
                    </FormInputValidationText>
                </div>
            )}
        </div>
    )
}
